import React, { memo } from 'react';
import {
  COLORS, LABEL_FONT, IMAGE_LABEL, TEXT_MIDDLE, TEXT_HANGING, BROKEN_IMAGE, STROKE_WIDTH,
} from '../../lib/network/constants';
import { NODE_BORDER_WIDTH } from '../../lib/geometry/nodes';
import { fitLabel, fitId } from '../../lib/display';
import { hasMath } from '../../lib/math';
import { TEXT_ABOVE, fitRichLabel, layoutRichLabel } from '../../lib/richLabel';

const HANDLE_HIT = 16;
const HANDLE_SIZE = 9;
const RESIZE_DIRS = ['n', 'e', 's', 'w'];
const RESIZE_CURSOR = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
};

function Shape({
  node, size, fill, stroke, imageError,
}) {
  const { width, height } = size;
  switch (node.shape) {
    case 'square':
      return (
        <rect
          x={-width / 2}
          y={-height / 2}
          width={width}
          height={height}
          fill={fill}
          stroke={stroke}
          strokeWidth={NODE_BORDER_WIDTH}
        />
      );
    case 'ellipse':
      return (
        <ellipse
          rx={width / 2}
          ry={height / 2}
          fill={fill}
          stroke={stroke}
          strokeWidth={NODE_BORDER_WIDTH}
        />
      );
    case 'box':
      return (
        <rect
          x={-width / 2}
          y={-height / 2}
          width={width}
          height={height}
          rx={6}
          ry={6}
          fill={fill}
          stroke={stroke}
          strokeWidth={NODE_BORDER_WIDTH}
        />
      );
    case 'image':
      return (
        <>
          <rect
            x={-width / 2}
            y={-height / 2}
            width={width}
            height={height}
            fill={fill}
            stroke={stroke}
            strokeWidth={NODE_BORDER_WIDTH}
          />
          <image
            href={imageError || !node.image ? BROKEN_IMAGE : node.image}
            x={-width / 2}
            y={-height / 2}
            width={width}
            height={height}
            preserveAspectRatio="xMidYMid meet"
          />
        </>
      );
    default:
      return (
        <circle
          r={width / 2}
          fill={fill}
          stroke={stroke}
          strokeWidth={NODE_BORDER_WIDTH}
        />
      );
  }
}

/** Lines of text and typeset maths, stacked downwards from `top` and centred on x = 0. */
function RichLines({
  layout, fontSize, top, halo,
}) {
  const haloProps = halo ? {
    stroke: 'white', strokeWidth: halo, strokeLinejoin: 'round', paintOrder: 'stroke',
  } : {};
  let y = top;
  return (
    <g>
      {layout.lines.map((line, lineIndex) => {
        const baseline = y + line.above;
        y = baseline + line.below;
        const left = -line.width / 2;
        return line.pieces.map((piece, index) => {
          const key = `${lineIndex}-${index}`;
          if (piece.type === 'math') {
            const { math } = piece;
            return (
              <svg
                key={key}
                x={left + piece.x}
                y={baseline - math.height * fontSize}
                width={math.width * fontSize}
                height={(math.height + math.depth) * fontSize}
                viewBox={math.viewBox}
                color={COLORS.label}
                overflow="visible"
                dangerouslySetInnerHTML={{ __html: math.body }} // eslint-disable-line react/no-danger
              />
            );
          }
          return (
            <text
              key={key}
              x={left + piece.x}
              y={baseline}
              fontSize={fontSize}
              fontFamily={LABEL_FONT.family}
              fill={COLORS.label}
              xmlSpace="preserve"
              {...haloProps} // eslint-disable-line react/jsx-props-no-spreading
            >
              {piece.value}
            </text>
          );
        });
      })}
    </g>
  );
}

function Label({ node, size }) {
  const caption = node.caption ?? node.label ?? '';
  const common = {
    textAnchor: 'middle',
    fontFamily: LABEL_FONT.family,
    fill: COLORS.label,
  };
  const rich = hasMath(caption);
  const chip = node.shape === 'dot' || node.shape === 'square';

  if (rich && chip) {
    // a circle's usable area is its inscribed ellipse
    const { fontSize, ...layout } = fitRichLabel(caption, node.shape === 'dot' ? 'ellipse' : 'box', size, LABEL_FONT.size, 8);
    return <RichLines layout={layout} fontSize={fontSize} top={-layout.height / 2} />;
  }

  if (rich && node.shape === 'image') {
    const layout = layoutRichLabel(caption, Infinity, LABEL_FONT.size);
    const top = size.height / 2 + IMAGE_LABEL.gap + LABEL_FONT.size * (TEXT_HANGING - TEXT_ABOVE);
    return (
      <RichLines layout={layout} fontSize={LABEL_FONT.size} top={top} halo={IMAGE_LABEL.halo} />
    );
  }

  if (rich) {
    const { fontSize, ...layout } = fitRichLabel(caption, node.shape, size);
    return <RichLines layout={layout} fontSize={fontSize} top={-layout.height / 2} />;
  }

  if (node.shape === 'image') {
    const lines = String(caption).split('\n');
    const lineHeight = LABEL_FONT.size * 1.2;
    const top = size.height / 2 + IMAGE_LABEL.gap + LABEL_FONT.size * TEXT_HANGING;
    return (
      <text
        fontSize={LABEL_FONT.size}
        stroke="white"
        strokeWidth={IMAGE_LABEL.halo}
        strokeLinejoin="round"
        paintOrder="stroke"
        {...common} // eslint-disable-line react/jsx-props-no-spreading
      >
        {lines.map((line, index) => (
          <tspan key={`${index}-${line}`} x={0} y={top + index * lineHeight}>{line}</tspan>
        ))}
      </text>
    );
  }

  if (chip) {
    const { fontSize } = fitId(caption, LABEL_FONT.size);
    return (
      <text
        y={fontSize * TEXT_MIDDLE}
        fontSize={fontSize}
        {...common} // eslint-disable-line react/jsx-props-no-spreading
      >
        {caption}
      </text>
    );
  }

  const { fontSize, lines, lineHeight } = fitLabel(caption, node.shape, size);
  return (
    <text
      fontSize={fontSize}
      {...common} // eslint-disable-line react/jsx-props-no-spreading
    >
      {lines.map((line, index) => (
        <tspan
          key={`${index}-${line}`}
          x={0}
          y={(index - (lines.length - 1) / 2) * lineHeight + fontSize * TEXT_MIDDLE}
        >
          {line}
        </tspan>
      ))}
    </text>
  );
}

function ResizeHandles({
  node, size, scale, color,
}) {
  const k = scale || 1;
  const hw = size.width / 2;
  const hh = size.height / 2;
  const at = {
    n: [0, -hh],
    e: [hw, 0],
    s: [0, hh],
    w: [-hw, 0],
  };
  return (
    <g>
      {RESIZE_DIRS.map((dir) => {
        const [x, y] = at[dir];
        const hit = HANDLE_HIT / k;
        const box = HANDLE_SIZE / k;
        return (
          <g
            key={dir}
            data-resize-dir={dir}
            style={{ cursor: RESIZE_CURSOR[dir] }}
          >
            <rect
              x={x - hit / 2}
              y={y - hit / 2}
              width={hit}
              height={hit}
              fill="transparent"
            />
            <rect
              x={x - box / 2}
              y={y - box / 2}
              width={box}
              height={box}
              fill="white"
              stroke={color}
              strokeWidth={STROKE_WIDTH / k}
            />
          </g>
        );
      })}
    </g>
  );
}

function NodeView({
  node, size, selected, hovered, imageError, scale = 1, showResize,
}) {
  const isImage = node.shape === 'image';
  const fill = node.color?.background || COLORS.nodeFill;
  const restStroke = node.color?.border || (isImage ? COLORS.imageBorder : COLORS.nodeStroke);
  let stroke = restStroke;
  if (selected) stroke = COLORS.highlight;
  else if (hovered) stroke = COLORS.hover;

  return (
    <g
      data-node-id={String(node.id)}
      transform={`translate(${node.x} ${node.y})`}
      style={{ cursor: 'pointer' }}
    >
      <Shape node={node} size={size} fill={fill} stroke={stroke} imageError={imageError} />
      <Label node={node} size={size} />
      {showResize && (
        <ResizeHandles node={node} size={size} scale={scale} color={stroke} />
      )}
    </g>
  );
}

export default memo(NodeView);
